import { format, isAfter, startOfToday, isValid } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { ja } from 'date-fns/locale'
import { TIME_ZONE_JP } from 'app/config'

const toValidDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const date = value instanceof Date ? value : new Date(value as string)
  return isValid(date) ? date : null
}

export const formatLocalDateTime = (date: Date | string) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(validDate, 'yyyy/M/d HH:mm:ss')
}

export const fulldateWeekFormat = (
  date: unknown,
  targetFormat: string = 'yyyy/M/d(EEE)',
) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat, {
    locale: ja,
  })
}
export const fullDateTimeWeekFormat = (
  date: unknown,
  targetFormat: string = 'yyyy/M/d(EEE) HH:mm',
) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat, {
    locale: ja,
  })
}
const dateTimeFormat = (
  date: Date,
  targetFormat: string = 'yyyy/M/d HH:mm',
) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat)
}
const dateFormat = (date: Date, targetFormat: string = 'yyyy/M/d') => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(validDate, targetFormat)
}
const dateFormatUTC = (date: Date, targetFormat: string = 'yyyy/M/d(EEE)') => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat, {
    locale: ja,
  })
}
const dateFormatToParamAPi = (
  date: Date,
  targetFormat: string = 'yyyy-MM-dd',
) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat)
}
const shortTimeFormatJp = (date: Date, targetFormat: string = 'HH:mm') => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat)
}
const shortTimeFormat = (date: Date, targetFormat: string = 'HH:mm') => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(validDate, targetFormat)
}

const isAfterNow = (date: Date) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return false
  }
  const now = startOfToday()
  return isAfter(validDate, now)
}
const estimatedTime = (timeStart: Date, timeEnd: Date) => {
  const validStart = toValidDate(timeStart)
  const validEnd = toValidDate(timeEnd)
  if (!validStart || !validEnd) {
    return 0
  }
  const difference =
    toZonedTime(validEnd, TIME_ZONE_JP).getTime() -
    toZonedTime(validStart, TIME_ZONE_JP).getTime() // This will give difference in milliseconds
  const resultInMinutes = Math.round(difference / 60000)
  return resultInMinutes
}
const dateFormatToJapan = (
  date: Date,
  targetFormat: string = 'yyyy年M月d日',
) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat)
}
const dateTimeFormatFullCalendar = (date: string, time: string) => {
  const validDate = toValidDate(date)
  if (!validDate) {
    return ''
  }
  const dateStr = toZonedTime(validDate, TIME_ZONE_JP)
  const dateFormatStr = dateStr.toISOString().replace(/T.*$/, '')
  return `${dateFormatStr}T${time}`
}

const dataTimePicker = () => {
  const results = []
  for (let i = 9; i <= 18; i += 1) {
    const hour = `0${i}`.slice(-2)
    if (i < 18) {
      for (let j = 0; j < 4; j += 1) {
        const minute = `${j === 0 ? `00` : 15 * j}`
        results.push({
          value: `${hour}:${minute}`,
          label: `${hour}:${minute}`,
        })
      }
    } else {
      results.push({ value: `${hour}:00`, label: `${hour}:00` })
    }
  }
  return results
}

const getLastDayOfNextMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 2, 0)

const currentInUtc = (
  date: Date,
  targetFormat: string = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
) => format(toZonedTime(new Date(date), TIME_ZONE_JP), targetFormat)
const formatTimeHourMinute = (time: string) => {
  const timeArr = time.split(':')
  return `${timeArr[0]}:${timeArr[1]}`
}

function checkTimeSlot(start: string, end: string) {
  const startHour = parseInt(start.split(':')[0], 10)
  const endHour = parseInt(end.split(':')[0], 10)
  if (
    startHour === endHour ||
    end.split(':')[1] === '00' ||
    end.split(':')[1] === '30'
  ) {
    return true
  }
  return false
}

const formatDateToJapanese = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-')
  const formattedDate = `${year}年${parseInt(month, 10)}月${parseInt(
    day,
    10,
  )}日`
  return formattedDate
}

const isoToJstDateTime = (
  isoString: string | undefined,
  targetFormat: string = 'yyyy/MM/dd HH:mm',
) => {
  const validDate = toValidDate(isoString)
  if (!validDate) {
    return ''
  }
  return format(toZonedTime(validDate, TIME_ZONE_JP), targetFormat)
}

export {
  toValidDate,
  dateTimeFormat,
  dateFormat,
  isAfterNow,
  estimatedTime,
  dateFormatToParamAPi,
  dateFormatToJapan,
  shortTimeFormatJp,
  dateTimeFormatFullCalendar,
  dataTimePicker,
  shortTimeFormat,
  dateFormatUTC,
  getLastDayOfNextMonth,
  currentInUtc,
  formatTimeHourMinute,
  checkTimeSlot,
  formatDateToJapanese,
  isoToJstDateTime,
}
